"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { ExternalLink } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { ImageDropzone } from "@/components/ImageDropzone";

/**
 * Admin inline market editor (2026-05-12). Replaces the translation-
 * only editor at /admin/markets/[slug]/translations as the canonical
 * one-stop edit surface — admin sees an "Edit market" button on
 * /markets/[slug] and a modal pops with every editable field.
 *
 * Backend: PATCH /v1/admin/markets/:id (STORY-903 editMarket). Accepts
 * any subset of:
 *   - title (i18n Record<locale, string>)
 *   - description (i18n Record<locale, string>)
 *   - imageUrl
 *   - resolutionSource (free-text)
 *   - closesAt (ISO 8601)
 *   - resolvesAt (ISO 8601)
 *
 * UX shape: modal Portal'd to document.body (per CLAUDE.md →
 * backdrop-filter creates a containing block for fixed positioning).
 * Each field has its own form row + a per-field Save button that fires
 * just that field's PATCH. Failures land per-row so partial saves
 * don't lose the rest of the admin's typing.
 *
 * Locale rotation: title + description default to 'en' as the source
 * locale. A locale dropdown lets admin pick pt-BR / es-MX / en-CA /
 * fr-CA / en-IN to add a translation in-place. The PATCH body sends
 * only the locale being edited; backend merges with the existing
 * JSONB column.
 */

const LOCALES = [
  { code: "en", label: "English (source)" },
  { code: "pt", label: "Portuguese (BR)" },
  { code: "es-MX", label: "Spanish (MX)" },
  { code: "en-CA", label: "English (CA)" },
  { code: "fr-CA", label: "French (CA)" },
  { code: "en-IN", label: "English (IN)" },
];

export interface EditableMarket {
  id: string;
  slug: string;
  title: Record<string, string | undefined>;
  description: Record<string, string | undefined>;
  imageUrl: string | null;
  resolutionSource: string;
  closesAt: string;
  resolvesAt: string;
}

interface Props {
  market: EditableMarket;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export function AdminMarketEditModal({ market, open, onClose, onSaved }: Props) {
  // One useState per field so the per-field save can update its own
  // baseline without forcing the others' form state to refresh. Loaded
  // once at modal-open from the `market` prop.
  const [locale, setLocale] = useState("en");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [resolutionSource, setResolutionSource] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [resolvesAt, setResolvesAt] = useState("");
  const [savingField, setSavingField] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<Record<string, string | null>>(
    {},
  );
  const [fieldSaved, setFieldSaved] = useState<Record<string, boolean>>({});

  // Reset form when modal opens or market changes.
  useEffect(() => {
    if (!open) return;
    setTitle(market.title[locale] ?? "");
    setDescription(market.description[locale] ?? "");
    setImageUrl(market.imageUrl ?? "");
    setResolutionSource(market.resolutionSource);
    // Trim ISO 8601 to the datetime-local format yyyy-MM-ddTHH:mm
    setClosesAt(market.closesAt.slice(0, 16));
    setResolvesAt(market.resolvesAt.slice(0, 16));
    setFieldError({});
    setFieldSaved({});
  }, [open, market, locale]);

  // Esc-to-close + click-outside-to-close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function saveField(field: string, body: Parameters<typeof api.editAdminMarket>[1]) {
    setSavingField(field);
    setFieldError({ ...fieldError, [field]: null });
    setFieldSaved({ ...fieldSaved, [field]: false });
    try {
      await api.editAdminMarket(market.id, body);
      setFieldSaved({ ...fieldSaved, [field]: true });
      onSaved?.();
      setTimeout(
        () => setFieldSaved((prev) => ({ ...prev, [field]: false })),
        2000,
      );
    } catch (err) {
      setFieldError({
        ...fieldError,
        [field]:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Save failed.",
      });
    } finally {
      setSavingField(null);
    }
  }

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit market"
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 px-3 py-6 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-[700px] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl">
        <header className="flex items-baseline justify-between gap-3 border-b border-[var(--color-border)] px-5 py-3">
          <div>
            <h2 className="text-[15px] font-bold text-[var(--color-text)]">
              Edit market
            </h2>
            <p className="mt-0.5 text-[11px] text-[var(--color-text3)]">
              Per-field save. Changes apply immediately + audit-logged.
              Slug: <span className="font-mono">{market.slug}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={`/admin/news-hub/markets/${market.id}`}
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] font-bold text-[var(--color-text2)] hover:text-[var(--color-brand)]"
              title="Open the full 3-tab editor (Setup / Content / Resolution)"
            >
              <ExternalLink size={11} />
              Full editor
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] font-bold text-[var(--color-text2)] hover:text-[var(--color-text)]"
            >
              Close
            </button>
          </div>
        </header>

        <div className="space-y-4 px-5 py-4">
          {/* Locale selector for title + description rows. */}
          <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
            Locale
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[12px] normal-case tracking-normal text-[var(--color-text)]"
            >
              {LOCALES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>

          <FieldRow
            label={`Title (${locale})`}
            error={fieldError.title}
            saved={fieldSaved.title}
            saving={savingField === "title"}
            onSave={() =>
              saveField("title", { title: { [locale]: title } })
            }
          >
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
          </FieldRow>

          <FieldRow
            label={`Description (${locale})`}
            error={fieldError.description}
            saved={fieldSaved.description}
            saving={savingField === "description"}
            onSave={() =>
              saveField("description", {
                description: { [locale]: description },
              })
            }
          >
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
          </FieldRow>

          <FieldRow
            label="Image"
            error={fieldError.imageUrl}
            saved={fieldSaved.imageUrl}
            saving={savingField === "imageUrl"}
            onSave={() =>
              saveField("imageUrl", {
                imageUrl: imageUrl.trim() === "" ? null : imageUrl,
              })
            }
          >
            <ImageDropzone
              scope="markets"
              value={imageUrl === "" ? null : imageUrl}
              onChange={(url) => setImageUrl(url ?? "")}
            />
          </FieldRow>

          <FieldRow
            label="Resolution source"
            error={fieldError.resolutionSource}
            saved={fieldSaved.resolutionSource}
            saving={savingField === "resolutionSource"}
            onSave={() =>
              saveField("resolutionSource", { resolutionSource })
            }
          >
            <input
              type="text"
              value={resolutionSource}
              onChange={(e) => setResolutionSource(e.target.value)}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
          </FieldRow>

          <FieldRow
            label="Closes at"
            error={fieldError.closesAt}
            saved={fieldSaved.closesAt}
            saving={savingField === "closesAt"}
            onSave={() => {
              const iso = new Date(closesAt).toISOString();
              return saveField("closesAt", { closesAt: iso });
            }}
          >
            <input
              type="datetime-local"
              value={closesAt}
              onChange={(e) => setClosesAt(e.target.value)}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
          </FieldRow>

          <FieldRow
            label="Resolves at"
            error={fieldError.resolvesAt}
            saved={fieldSaved.resolvesAt}
            saving={savingField === "resolvesAt"}
            onSave={() => {
              const iso = new Date(resolvesAt).toISOString();
              return saveField("resolvesAt", { resolvesAt: iso });
            }}
          >
            <input
              type="datetime-local"
              value={resolvesAt}
              onChange={(e) => setResolvesAt(e.target.value)}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
          </FieldRow>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function FieldRow({
  label,
  children,
  saving,
  saved,
  error,
  onSave,
}: {
  label: string;
  children: React.ReactNode;
  saving: boolean;
  saved?: boolean;
  error?: string | null;
  onSave: () => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </label>
      {children}
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-md border border-[var(--color-brand)] bg-[var(--color-brand-tint)] px-3 py-1 text-[11px] font-bold text-[var(--color-brand)] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {saved && (
          <span className="text-[11px]" style={{ color: "var(--color-yes)" }}>
            ✓ Saved
          </span>
        )}
        {error && (
          <span
            role="alert"
            className="text-[11px]"
            style={{ color: "var(--color-no)" }}
          >
            {error}
          </span>
        )}
      </div>
    </div>
  );
}
